import { Box, Button, Grid, makeStyles, MenuItem, Select } from "@material-ui/core";
import { SetLoading } from "common/components/Loader/Loader";
import UserCard from "common/components/UserCard";
import StyleHelper from "common/helpers/styleHelper";
import { NameplateStyle } from "common/types/NameplateStyle";
import UserManager from "common/userManager";
import * as React from "react";
import NameplateService from "services/NameplateService";
import { Colors } from "styles/Colors";
import NameplateTypeList from "./NameplateTypeList";

const useStyles = makeStyles((theme) => ({
	cssEditor: {
		backgroundColor: Colors.panelLight,
		color: Colors.primary,
		width: 600,
		height: 312,
		font: "normal normal 16px/24px Arial",
	},
	nameplateDrawer: {},
}));

const nameplateTemplate = `{id}.nameplate-container {
    border: 1px solid #ECECEC;
    background-color: #474747;
    color: #ECECEC;
}

{id} .nameplate-name {

}

{id} .nameplate-trophy {

}`;

export default function NameplateEditor() {
	const styles = useStyles();
	const userId = UserManager.getUserId();
	/** @type {[NameplateStyle, any]} */
	const [nameplate, setNameplate] = React.useState(null);
	const [nameplateList, setNameplateList] = React.useState([]);
	const [typeList, setTypeList] = React.useState([]);
	const [name, setName] = React.useState("");
	const [description, setDescription] = React.useState("");
	const [type, setType] = React.useState("");
	const [template, setTemplate] = React.useState(nameplateTemplate);
	/** @type {[HTMLStyleElement, any]} */
	const [styleRef, setStyleRef] = React.useState(null);
	const [recomputeUserId, setRecomputeUserId] = React.useState("");

	React.useEffect(() => {
		SetLoading(true);
		Promise.all([
			NameplateService.loadNameplates().then((nameplates) => {
				setNameplateList(nameplates);
			}),
			NameplateService.getTypes().then((types) => {
				setTypeList(types);
			}),
		]).then(() => {
			SetLoading(false);
		});
	});

	React.useEffect(() => {});

	const { sections, untyped } = React.useMemo(() => {
		const byType = new Map();
		const untypedList = [];
		for (const nameplate of nameplateList) {
			if (nameplate.namePlateTypeId == null) {
				untypedList.push(nameplate);
				continue;
			}
			const bucket = byType.get(nameplate.namePlateTypeId);
			if (bucket) {
				bucket.push(nameplate);
			} else {
				byType.set(nameplate.namePlateTypeId, [nameplate]);
			}
		}
		const grouped = typeList
			.filter((t) => byType.has(t.id))
			.map((t) => ({ type: t, nameplates: byType.get(t.id) }));
		return { sections: grouped, untyped: untypedList };
	}, [nameplateList, typeList]);

	const updateStyle = (updatedTemplate) => {
		updatedTemplate = updatedTemplate || template;

		// @ts-ignore
		let testTemplate = updatedTemplate.replaceAll("{id}", ".nameplate-test");
		if (styleRef) {
			styleRef.textContent = testTemplate;
			return;
		}

		const newStyleRef = StyleHelper.addStyle(testTemplate);
		setStyleRef(newStyleRef);
	};

	const onCreateNameplate = () => {
		setNameplate(new NameplateStyle());
		setName("");
		setDescription("");
		setType("");
		setTemplate(nameplateTemplate);
		updateStyle(nameplateTemplate);
	};

	/**
	 *
	 * @param {NameplateStyle} nameplate
	 */
	const onSelectNameplate = (nameplate) => {
		setNameplate(nameplate);
		setName(nameplate.name);
		setDescription(nameplate.description);
		setType(nameplate.namePlateTypeId ?? "");
		setTemplate(nameplate.style);
		updateStyle(nameplate.style);
	};

	const onApply = () => {
		updateStyle();
	};

	const onSave = () => {
		nameplate.name = name;
		nameplate.description = description;
		nameplate.style = template;
		nameplate.namePlateTypeId = type === "" ? null : type;

		SetLoading(true, "Saving Nameplate");
		NameplateService.saveNameplate(nameplate).then((saved) => {
			NameplateService.loadNameplates().then((nameplates) => {
				setNameplateList(nameplates);
				SetLoading(false);
				onSelectNameplate(saved);
			});
		});
	};

	const onDelete = () => {
		if (!window.confirm(`Delete nameplate "${name}"?`)) {
			return;
		}

		SetLoading(true, "Deleting Nameplate");
		NameplateService.deleteNameplate(nameplate.id).then(() => {
			NameplateService.loadNameplates().then((nameplates) => {
				setNameplateList(nameplates);
				setNameplate(null);
				SetLoading(false);
			});
		});
	};

	const onChangeName = (event) => {
		const value = event.currentTarget.value;
		setName(value);
	};

	const onChangeDescription = (event) => {
		const value = event.currentTarget.value;
		setDescription(value);
	};

	const onChangeTemplate = (event) => {
		const value = event.currentTarget.value;
		setTemplate(value);
	};

	const onChangeType = (event) => {
		setType(event.target.value);
	};

	const onRecomputeAll = () => {
		if (
			!window.confirm(
				"Recompute achievement nameplates for ALL users? This runs in the background."
			)
		) {
			return;
		}
		NameplateService.recomputeAllAchievements()
			.then(() => {
				window.alert("Recompute queued. It runs in the background.");
			})
			.catch((error) => {
				window.alert(`Recompute failed: ${error}`);
			});
	};

	const onRecomputeUser = () => {
		const id = parseInt(recomputeUserId, 10);
		if (!id) {
			window.alert("Enter a valid user id.");
			return;
		}
		SetLoading(true, "Recomputing achievements");
		NameplateService.recomputeUserAchievements(id)
			.then(() => {
				window.alert(`Recomputed achievements for user ${id}.`);
			})
			.catch((error) => {
				window.alert(`Recompute failed: ${error}`);
			})
			.finally(() => {
				SetLoading(false);
			});
	};

	return (
		<Grid container lg={12}>
			<Grid item lg={2}>
				<Box p={2} style={{ paddingLeft: 26 }}>
					<h3>Achievements</h3>
					<p style={{ fontSize: 12, lineHeight: 1.4 }}>
						{
							"Recompute scans a user's results and grants any achievement nameplates they've earned. Mainly for testing and the initial rollout - nameplates should be granted automatically when an event's trophies are given."
						}
					</p>
					<Box
						display="flex"
						flexDirection="column"
						alignItems="flex-start"
						style={{ gap: 8 }}
					>
						<input
							placeholder="User id"
							value={recomputeUserId}
							onChange={(e) => setRecomputeUserId(e.currentTarget.value)}
						/>
						<Button
							variant="contained"
							color="primary"
							onClick={onRecomputeUser}
						>
							Recompute user
						</Button>
					</Box>
					<Box mt={6}>
						<p style={{ fontSize: 12, lineHeight: 1.4 }}>
							{
								"Recomputing all users can take several minutes and runs in the background."
							}
						</p>
						<Button
							variant="contained"
							color="primary"
							onClick={onRecomputeAll}
						>
							Recompute all users
						</Button>
					</Box>
				</Box>
			</Grid>
			<Grid item lg={8}>
				<Grid container direction="column" alignItems="center">
					{!nameplate && <h1>MAKE A NAMEPLATE DAMNIT</h1>}
					{nameplate && (
						<>
							<Grid item sm={12} style={{ display: "inline-block" }}>
								<Box mb={12} mt={12}>
									<UserCard userId={userId} nameplateId={"test"} />
								</Box>
							</Grid>
							<Grid item sm={12}>
								<Box>
									<table>
										<tbody>
											<tr>
												<td>
													<label>Name</label>
												</td>
												<td>
													<input value={name} onChange={onChangeName} />
												</td>
											</tr>
											<tr>
												<td>
													<label>Description</label>
												</td>
												<td>
													<input
														value={description}
														onChange={onChangeDescription}
													/>
												</td>
											</tr>
											<tr>
												<td>
													<label>Type</label>
												</td>
												<td>
													<Select
														value={type}
														onChange={onChangeType}
														displayEmpty
													>
														<MenuItem value="">
															<em>None</em>
														</MenuItem>
														{typeList.map((type) => (
															<MenuItem value={type.id}>
																<em>{type.name}</em>
															</MenuItem>
														))}
													</Select>
												</td>
											</tr>
										</tbody>
									</table>
								</Box>
							</Grid>
							<Grid item sm={12}>
								<Box>
									<label>Style</label>
								</Box>
								<Box>
									<textarea
										value={template}
										className={styles.cssEditor}
										onChange={onChangeTemplate}
									/>
								</Box>
							</Grid>
							<Grid item sm={12}>
								<Box display="flex" alignItems="center" width={600}>
									<Box flex={1} display="flex" justifyContent="flex-start">
										{!!nameplate.id && (
											<Button
												variant="contained"
												onClick={onDelete}
												style={{
													backgroundColor: Colors.error,
													color: Colors.white,
												}}
											>
												Delete
											</Button>
										)}
									</Box>
									<Box display="flex" style={{ gap: 8 }}>
										<Button variant="contained" color="primary" onClick={onApply}>
											Apply
										</Button>
										<Button variant="contained" color="primary" onClick={onSave}>
											Save
										</Button>
									</Box>
									<Box flex={1} />
								</Box>
							</Grid>
						</>
					)}
				</Grid>
			</Grid>
			<Grid item lg={2}>
				<Box className={styles.nameplateDrawer}>
					<NameplateTypeList
						sections={sections}
						untyped={untyped}
						onSelectNameplate={onSelectNameplate}
						onCreateNameplate={onCreateNameplate}
					/>
				</Box>
			</Grid>
		</Grid>
	);
}
